import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { BarChart2 } from "lucide-react";

const COLORS = [
  { bar: "bg-green-500", text: "text-green-400" },
  { bar: "bg-yellow-400", text: "text-yellow-400" },
  { bar: "bg-blue-500", text: "text-blue-400" },
  { bar: "bg-red-500", text: "text-red-400" },
  { bar: "bg-purple-500", text: "text-purple-400" },
];

export default function CommunityPoll() {
  const [poll, setPoll] = useState(null);
  const [voted, setVoted] = useState(false);
  const [votedOption, setVotedOption] = useState(null);
  const [voting, setVoting] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPoll = () => {
    api.get("/polls/active")
      .then((r) => {
        setPoll(r.data);
        const saved = localStorage.getItem("poll_voted_" + r.data.id);
        if (saved) { setVoted(true); setVotedOption(saved); }
      })
      .catch(() => setPoll(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPoll();
    const interval = setInterval(fetchPoll, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading || !poll) return null;

  const total = poll.options.reduce((sum, o) => sum + o.votes, 0);
  const daysLeft = poll.ends_at
    ? Math.max(0, Math.ceil((new Date(poll.ends_at) - new Date()) / 86400000))
    : null;

  const handleVote = async (option) => {
    if (voted || voting) return;
    setVoting(true);
    try {
      const { data } = await api.post("/polls/" + poll.id + "/vote", { option });
      setPoll(data);
      setVoted(true);
      setVotedOption(option);
      localStorage.setItem("poll_voted_" + poll.id, option);
    } catch {} finally { setVoting(false); }
  };

  return (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <BarChart2 className="w-4 h-4 text-yellow-400" />
        <h3 className="font-display font-bold text-sm uppercase tracking-wide text-[var(--text-primary)]">Community Poll</h3>
        <span className="ml-auto flex items-center gap-1 text-[10px] font-mono text-green-400">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> Live
        </span>
      </div>

      <p className="text-sm font-bold text-[var(--text-primary)] mb-4">{poll.question}</p>

      <div className="space-y-3">
        {poll.options.map((option, index) => {
          const pct = total > 0 ? Math.round((option.votes / total) * 100) : 0;
          const c = COLORS[index % COLORS.length];
          const isMine = votedOption === option.label;
          return (
            <button key={option.label} onClick={() => handleVote(option.label)} disabled={voted || voting}
              className={"w-full text-left disabled:cursor-default " + (isMine ? "opacity-100" : "")}>
              <div className="flex items-center justify-between mb-1">
                <span className={"text-xs " + (isMine ? "text-[var(--text-primary)] font-bold" : "text-[var(--text-secondary)]")}>
                  {isMine && "✓ "}{option.label}
                </span>
                {voted && <span className={"text-xs font-mono font-bold " + c.text}>{pct}%</span>}
              </div>
              <div className="h-2 bg-[var(--bg-elevated)] rounded-full overflow-hidden">
                <div className={"h-full rounded-full transition-all duration-700 " + c.bar} style={{ width: (voted ? pct : 0) + "%" }} />
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-[var(--border-soft)]">
        <span className="text-xs font-mono text-[var(--text-tertiary)]">
          {total.toLocaleString()} votes{daysLeft !== null ? ` · Ends in ${daysLeft}d` : ""}
        </span>
        {!voted && (
          <span className="text-xs font-mono text-yellow-400">Tap to vote</span>
        )}
      </div>
    </div>
  );
}